import { Deal } from "@/types/deal";

export function calculateFinancials(deal: Deal) {
  try {
    const kpis = deal.kpis || {};

    if (!kpis.roomRevenue || !kpis.ramp || !kpis.investmentBudget) {
      console.warn("Missing required KPI data");
      return {
        year3Revenue: 0,
        year3YieldOnCost: 0,
        projectIRR: 0,
      };
    }

    const totalCost = kpis.investmentBudget?.grandTotal || 0;

    const baseRevenue = kpis.roomRevenue?.totals?.roomsRevenue || 0;
    const baseADR = kpis.roomRevenue?.totals?.avgADR || 20;
    const baseOccPct = kpis.roomRevenue?.totals?.avgOccPct || 75.38;
    const rooms = kpis.rooms?.total || 22;
    const days = 365;

    // Ramp настройки
    const rampSettings = kpis.rampSettings;
    const revenueRamp = rampSettings?.revenueRamp || [0.7, 0.8, 0.9, 1.0];
    const toplineGrowthPct = rampSettings?.toplineGrowthPct || 2;
    const growthRate = toplineGrowthPct / 100;

    // Year 3 расчет
    const yearNum = 3;
    const revRampMul = yearNum <= 4 ? revenueRamp[yearNum - 1] : 1.0;
    const growthIndex = Math.pow(1 + growthRate, yearNum);

    // ADR Year 3
    const adrY3 = baseADR * revRampMul * growthIndex;

    // Occupancy Year 3
    const occFractionY3 = (baseOccPct / 100) * revRampMul;

    // Rooms Revenue Year 3
    const roomsAvailableY3 = rooms * days;
    const roomsSoldY3 = roomsAvailableY3 * occFractionY3;
    const roomsRevenueY3 = adrY3 * roomsSoldY3;

    // 3. Другие доходы (упрощенно)
    // F&B Revenue - 20% от Rooms Revenue
    const fnbRevenueY3 = roomsRevenueY3 * 0.2;

    // Other Revenue - 5% от Rooms Revenue (из ваших данных)
    const otherRevenueY3 = roomsRevenueY3 * 0.05;

    const spaRevenueY3 = 4 * 70 * 365 * revRampMul * growthIndex; // 4 treatments × $70 × 365

    const year3Revenue =
      roomsRevenueY3 + fnbRevenueY3 + otherRevenueY3 + spaRevenueY3;

    const ebitdaY3 = year3Revenue * 0.3;

    // 5. Year 3 Yield on Cost
    const year3YieldOnCost = totalCost > 0 ? (ebitdaY3 / totalCost) * 100 : 0;

    // 6. Project IRR (упрощенный расчет)
    // Используем данные из deal.kpis.exit
    const exitYear = kpis.exit?.sale?.exitYear || 3;
    const exitCapRate = kpis.exit?.sale?.exitCapRate || 5.5;

    // EBITDA на год выхода
    const ebitdaExit = year3Revenue * 0.3;
    const exitValue = ebitdaExit / (exitCapRate / 100);

    // Cash flows
    const cashFlows = [-totalCost]; // Year 0
    for (let year = 1; year <= exitYear; year++) {
      const yearlyRevenue = year3Revenue * Math.pow(1 + growthRate, year - 3);
      const yearlyEBITDA = yearlyRevenue * 0.3;

      if (year === exitYear) {
        cashFlows.push(yearlyEBITDA + exitValue); // Exit year
      } else {
        cashFlows.push(yearlyEBITDA);
      }
    }

    const projectIRR = calculateIRR(cashFlows);

    return {
      year3Revenue,
      year3YieldOnCost,
      projectIRR,
      totalInvestment: totalCost,
    };
  } catch (error) {
    return {
      year3Revenue: 0,
      year3YieldOnCost: 0,
      projectIRR: 0,
      totalInvestment: 0,
    };
  }
}

function calculateIRR(cashFlows: number[]): number {
  if (cashFlows.length < 2) return 0;

  const maxIterations = 1000;
  const tolerance = 0.00001;

  let lower = -0.9999;
  let upper = 1.0;
  let irr = (lower + upper) / 2;

  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    for (let t = 0; t < cashFlows.length; t++) {
      npv += cashFlows[t] / Math.pow(1 + irr, t);
    }

    if (Math.abs(npv) < tolerance) {
      return irr * 100; // Convert to percentage
    }

    if (npv > 0) {
      lower = irr;
    } else {
      upper = irr;
    }

    irr = (lower + upper) / 2;
  }

  return irr * 100;
}
